import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import LocalDiningRoundedIcon from "@mui/icons-material/LocalDiningRounded";
import SearchProduct from "../AdminProducts/Products/SearchProduct";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";
import { useAlert } from "../../../context/AlertContext";

function formatMoney(value) {
  const number = Number(value || 0);

  return number.toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMoney6(value) {
  const number = Number(value || 0);

  return number.toLocaleString("es-EC", {
    minimumFractionDigits: 6,
    maximumFractionDigits: 6,
  });
}

function round2(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function round6(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 1000000) / 1000000;
}

function normalizeIva(value) {
  const iva = Number(value || 0);

  if (iva === 999) return 999;
  if ([0, 5, 15].includes(iva)) return iva;

  return 0;
}

function getProductFinalPrice(product) {
  return Number(
    product?.precio_final ??
      product?.pvp1 ??
      product?.price ??
      product?.precio ??
      0
  );
}

function getProductIva(product) {
  return normalizeIva(
    product?.iva_porcentaje ??
      product?.iva ??
      product?.iva_percent ??
      0
  );
}

function getIvaLabel(value) {
  if (Number(value) === 999) return "No objeto";
  return `${Number(value || 0)}%`;
}

function calculateLineFromFinalPrice(precioConIva, ivaPorcentaje, cantidad) {
  const finalUnitario = Number(precioConIva || 0);
  const qty = Number(cantidad || 0);
  const iva = normalizeIva(ivaPorcentaje);
  const totalFinal = round2(finalUnitario * qty);

  if (iva === 999 || iva <= 0) {
    return {
      precioSinIva: round6(finalUnitario),
      precioConIva: finalUnitario,
      ivaUnitario: 0,
      totalSinIva: totalFinal,
      ivaTotal: 0,
      total: totalFinal,
    };
  }

  const totalSinIvaExacto = totalFinal / (1 + iva / 100);
  const totalSinIvaSri = round2(totalSinIvaExacto);
  const ivaTotalSri = round2(totalFinal - totalSinIvaSri);
  const precioSinIva = qty > 0 ? round6(totalSinIvaExacto / qty) : 0;
  const ivaUnitario = qty > 0 ? round6(ivaTotalSri / qty) : 0;

  return {
    precioSinIva,
    precioConIva: finalUnitario,
    ivaUnitario,
    totalSinIva: totalSinIvaSri,
    ivaTotal: ivaTotalSri,
    total: totalFinal,
  };
}

export default function ProductDetail({ onAddDetail, disabled = false }) {
  const { dashboardTheme } = useDashboardTheme();
  const { showError } = useAlert();

  const token = localStorage.getItem("accessToken");

  const [sabores, setSabores] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cantidad, setCantidad] = useState("1");
  const [observacionDetalle, setObservacionDetalle] = useState("");
  const [saboresSeleccionados, setSaboresSeleccionados] = useState([]);

  const loadSabores = async () => {
    try {
      const response = await fetch("/api/ventas/sabores/?activo=true", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudieron cargar los sabores");
        return;
      }

      setSabores(data.results || []);
    } catch {
      showError("Error de conexión cargando sabores");
    }
  };

  useEffect(() => {
    loadSabores();
  }, []);

  const toggleSabor = (saborId) => {
    setSaboresSeleccionados((prev) => {
      if (prev.includes(saborId)) {
        return prev.filter((id) => id !== saborId);
      }

      return [...prev, saborId];
    });
  };

  const handleAdd = () => {
    if (!selectedProduct?.id) {
      showError("Selecciona un producto desde el buscador.");
      return;
    }

    const cantidadNumber = Number(cantidad || 0);

    if (cantidadNumber <= 0) {
      showError("La cantidad debe ser mayor a cero.");
      return;
    }

    const precioConIva = getProductFinalPrice(selectedProduct);
    const ivaPorcentaje = getProductIva(selectedProduct);

    const calculo = calculateLineFromFinalPrice(
      precioConIva,
      ivaPorcentaje,
      cantidadNumber
    );

    const saboresDetalle = sabores
      .filter((sabor) => saboresSeleccionados.includes(sabor.id))
      .map((sabor) => ({
        id: sabor.id,
        sabor_id: sabor.id,
        sabor_nombre: sabor.nombre,
        nombre: sabor.nombre,
      }));

    onAddDetail({
      uid: `${selectedProduct.id}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      producto: selectedProduct.id,
      producto_id: selectedProduct.id,
      producto_nombre: selectedProduct.name || selectedProduct.nombre || "",
      descripcion: selectedProduct.name || selectedProduct.nombre || "",
      cantidad: cantidadNumber,
      precio_unitario: calculo.precioSinIva,
      precio_sin_iva: calculo.precioSinIva,
      precio_con_iva: calculo.precioConIva,
      iva_porcentaje: ivaPorcentaje,
      iva_unitario: calculo.ivaUnitario,
      total_sin_iva: calculo.totalSinIva,
      iva_total: calculo.ivaTotal,
      total: calculo.total,
      observacion: observacionDetalle.trim(),
      sabores: saboresDetalle,
      sabores_ids: saboresSeleccionados,
      image: selectedProduct.image || "",
    });

    setSelectedProduct(null);
    setCantidad("1");
    setObservacionDetalle("");
    setSaboresSeleccionados([]);
  };

  const cardSx = {
    p: { xs: 1.5, sm: 2, md: 2.5 },
    borderRadius: "14px",
    border: `1px solid ${dashboardTheme.border_color}`,
    bgcolor: dashboardTheme.card_background,
    boxShadow: "none",
  };

  const selectedProductCard = useMemo(() => {
    if (!selectedProduct) return null;

    const precioConIva = getProductFinalPrice(selectedProduct);
    const ivaPorcentaje = getProductIva(selectedProduct);
    const calculo = calculateLineFromFinalPrice(precioConIva, ivaPorcentaje, 1);
    const productDescription = selectedProduct.description || selectedProduct.descripcion || "";

    return (
      <Box sx={{ ...cardSx, mt: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "92px minmax(0, 1fr) 120px minmax(360px, 1fr) auto",
            },
            gap: 1.5,
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: 92 },
              height: 92,
              borderRadius: "14px",
              border: `1px solid ${dashboardTheme.border_color}`,
              bgcolor:
                dashboardTheme.theme_mode === "dark"
                  ? "rgba(255,255,255,0.04)"
                  : "rgba(17,24,39,0.04)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {selectedProduct.image ? (
              <Box
                component="img"
                src={selectedProduct.image}
                alt={selectedProduct.name || selectedProduct.nombre}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  p: 1,
                }}
              />
            ) : (
              <LocalDiningRoundedIcon
                sx={{
                  fontSize: 44,
                  color: dashboardTheme.muted_text_color,
                }}
              />
            )}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: { xs: 18, md: 20 },
                fontWeight: 700,
                color: dashboardTheme.text_color,
                lineHeight: 1.15,
                wordBreak: "break-word",
              }}
            >
              {selectedProduct.name || selectedProduct.nombre}
            </Typography>

            {productDescription && (
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: dashboardTheme.muted_text_color,
                  mt: 0.7,
                  lineHeight: 1.35,
                  whiteSpace: "pre-line",
                }}
              >
                {productDescription}
              </Typography>
            )}

            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: dashboardTheme.primary_color,
                mt: 0.8,
              }}
            >
              PVP cliente: ${formatMoney(precioConIva)}
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: dashboardTheme.muted_text_color,
                mt: 0.3,
              }}
            >
              SRI unitario: ${formatMoney6(calculo.precioSinIva)} · IVA {getIvaLabel(ivaPorcentaje)}
            </Typography>
          </Box>

          <TextField
            size="small"
            label="Cantidad"
            value={cantidad}
            onChange={(e) => setCantidad(e.target.value)}
            disabled={disabled}
            sx={{
              width: { xs: "100%", md: 120 },
              ...dashboardTheme.inputStyles,
            }}
          />

          <TextField
            fullWidth
            size="small"
            label="Detalle adicional"
            value={observacionDetalle}
            onChange={(e) => setObservacionDetalle(e.target.value)}
            placeholder="Ej: crepe, galleta, sin chocolate"
            disabled={disabled}
            sx={{
              minWidth: { xs: "100%", md: 360 },
              ...dashboardTheme.inputStyles,
            }}
          />

          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={handleAdd}
            disabled={disabled}
            sx={{
              ...dashboardTheme.buttonStyles.contained,
              minHeight: 42,
              whiteSpace: "nowrap",
            }}
          >
            Añadir
          </Button>
        </Box>

        <Divider sx={{ my: 2, borderColor: dashboardTheme.border_color }} />

        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 700,
            color: dashboardTheme.text_color,
            mb: 1,
          }}
        >
          Sabores del helado
        </Typography>

        {sabores.length === 0 && (
          <Typography
            sx={{
              fontSize: 14,
              color: dashboardTheme.muted_text_color,
            }}
          >
            No hay sabores activos registrados.
          </Typography>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: 0.5,
          }}
        >
          {sabores.map((sabor) => (
            <FormControlLabel
              key={sabor.id}
              control={
                <Checkbox
                  checked={saboresSeleccionados.includes(sabor.id)}
                  onChange={() => toggleSabor(sabor.id)}
                  disabled={disabled}
                  sx={{
                    color: dashboardTheme.muted_text_color,
                    "&.Mui-checked": {
                      color: dashboardTheme.primary_color,
                    },
                  }}
                />
              }
              label={sabor.nombre}
              sx={{
                color: dashboardTheme.text_color,
                m: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: 14,
                },
              }}
            />
          ))}
        </Box>
      </Box>
    );
  }, [
    selectedProduct,
    cantidad,
    observacionDetalle,
    sabores,
    saboresSeleccionados,
    dashboardTheme,
    disabled,
  ]);

  return (
    <Box>
      <SearchProduct
        showEcommerceCheck
        onSelectProduct={(product) => {
          setSelectedProduct(product);
          setCantidad("1");
          setObservacionDetalle("");
          setSaboresSeleccionados([]);
        }}
      />

      {selectedProductCard}
    </Box>
  );
}